import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  effect,
} from '@angular/core';
import { Candidates } from '@interfaces/candidates.model';
import { IPaginationParams, pageData, IEmeraldTabData } from '@emerald/nxcore';
import { paginationParams, tabsData } from '@shared/config/table.config';
import { columnDefs } from './hiring-manager-candidates.config';
import { HiringManagerCandidatesService } from './hiring-manager-candidates.service';
import { GlobalErrorService } from '@services/global-error.service';
import { FileDownloadService } from '@services/file-download.service';
import { SearchService } from '@services/search.service';
import { PersonaType } from '@constants/global.enum';
import { dateFormat } from '@constants/dateFormat';
import { employeeLevelCode } from '@constants/constants';
import { interval, takeWhile, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HiringManagerOpenpositionsService } from '../hiring-manager-openpositions/hiring-manager-openpositions.service';
import { PositionData } from '@app/shared/interfaces/open-positions.model';
import { mapHiringRequest } from '../hiring-manager-openpositions/hiring-manager-openpositions.helper';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-hiring-manager-candidates',
  templateUrl: './hiring-manager-candidates.component.html',
  styleUrls: ['./hiring-manager-candidates.component.scss'],
})
export class HiringManagerCandidatesComponent implements OnChanges, OnDestroy {
  @Input() candidates: Candidates[] = [];
  @Input() selectedTab!: IEmeraldTabData;
  @Input() candidatesCount!: number;
  @Input() hiringRequestId!: number[];
  @Input() employeeLevel!: number;
  @Output() candidatesCountChange = new EventEmitter<number>();
  @Output() candidatesDataChange = new EventEmitter<Candidates[]>();

  columnDefs = columnDefs;
  errorMessage: string | null = null;
  displayCandidatesData: Candidates[] = [];
  tabsData = tabsData;
  fieldsVisibility: string = PersonaType.hiringMgrCandidates;
  fileUrl: string = 'https://pdfobject.com/pdf/sample.pdf';
  filterText: string = '';
  urlDisplayLimit: number = 15;
  imageSrc: string = 'assets/img/no-data-found.png';
  isDataLoaded: boolean = false;
  dateFormat = dateFormat;
  openPositions: PositionData[] = [];

  searchFieldOptions = [
    { label: 'Candidate Name', value: 'candidateName' },
    { label: 'Position', value: 'jobTitle' },
    { label: 'Status', value: 'status' },
    { label: 'Location', value: 'location' },
    { label: 'Email', value: 'emailId' },
  ];
  selectedSearchField = '';
  searchFieldDropdownVisible = true;
  private currentSearchKey = '';
  private currentSearchValue = '';
  filterValue = '';

  // ---- op-table client-side sort / pagination / tooltip state ----
  sortColumn = '';
  sortDir: 'asc' | 'desc' = 'asc';
  currentServerPage = 1;
  localPageSize = paginationParams.pageSize;
  hoverTip = { show: false, text: '', x: 0, y: 0 };

  private initLoadCount = 0;
  private readonly INIT_FIRES = 3;
  private isDataChanging = false;
  private isClearing = false;
  private searchSubject = new Subject<string>();
  
  paginationParams: IPaginationParams = {
    ...paginationParams,
    serverSide: true,
    total: 0,
    onChange: (page: pageData) => this.onPageChange(page),
  };

  @ViewChild('historyDialog') historyDialogTpl!: TemplateRef<any>;
  private historyDialogRef?: MatDialogRef<any>;
  historyMessage: string = '';
  historySubmissions: Array<{
    jobId: string;
    jobTitle: string;
    hiringManager: string;
    status: string;
    feedback: string;
    vendorName: string;
    submittedDate: string;
  }> = [];

  truncateText(text: string, maxLength: number = 30): string {
    if (!text || text === 'null' || text === 'undefined') return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  // ---- vendor-style inline filter panel (client-side over the loaded candidates,
  // the same working set the previous shared filter operated on) ----
  showFilterPanel = false;
  filterDraft = { status: '', jobTitle: '', location: '', experience: '' };
  activeFilters = { status: '', jobTitle: '', location: '', experience: '' };

  get facetStatus(): string[] { return this.facetOf('status'); }
  get facetPosition(): string[] { return this.facetOf('jobTitle'); }
  get facetLocation(): string[] { return this.facetOf('location'); }
  get facetExperience(): string[] {
    const vals = (this.candidates || [])
      .map((c: any) => c?.yearsOfExperience)
      .filter((v: any) => v !== null && v !== undefined && `${v}`.trim() !== '');
    return Array.from(new Set(vals)).sort((a: any, b: any) => a - b).map(String);
  }
  private facetOf(key: string): string[] {
    const vals = (this.candidates || [])
      .map((c: any) => (c?.[key] ?? '').toString().trim())
      .filter((v: string) => v && v.toLowerCase() !== 'null' && v.toLowerCase() !== 'undefined');
    return Array.from(new Set(vals)).sort((a, b) => a.localeCompare(b));
  }
  get activeFilterCount(): number {
    return Object.values(this.activeFilters).filter((v) => !!v).length;
  }

  toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    if (this.showFilterPanel) {
      this.filterDraft = { ...this.activeFilters };
    }
  }

  applyFilterPanel(): void {
    this.activeFilters = { ...this.filterDraft };
    this.showFilterPanel = false;
    this.applyLocalFilters();
  }

  resetFilterPanel(): void {
    this.filterDraft = { status: '', jobTitle: '', location: '', experience: '' };
    this.activeFilters = { ...this.filterDraft };
    this.showFilterPanel = false;
    this.applyLocalFilters();
  }

  private applyLocalFilters(): void {
    const f = this.activeFilters;
    let rows: any[] = [...(this.candidates || [])];
    if (f.status) rows = rows.filter((r) => r?.status === f.status);
    if (f.jobTitle) rows = rows.filter((r) => r?.jobTitle === f.jobTitle);
    if (f.location) rows = rows.filter((r) => r?.location === f.location);
    if (f.experience) rows = rows.filter((r) => String(r?.yearsOfExperience) === f.experience);
    this.displayCandidatesData = rows as Candidates[];
    this.cdr.detectChanges();
  }

  // Maps a candidate status to a pill tone (matches the vendor-flow status pills).
  statusPillClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (/(joined|selected|shortlist|hire|in progress|onboard(?!.*pending))/.test(s)) return 'ok';
    if (/(reject|dropped|backed out|declined|no show)/.test(s)) return 'crit';
    if (/(pending|review|schedule|hold|await|requested)/.test(s)) return 'warn';
    return 'cold';
  }

  getTooltipText(text: string): string | null {
    if (!text || text === 'null' || text === 'undefined' || text.trim() === '') return null;
    return text;
  }

  constructor(
    private candidatesService: HiringManagerCandidatesService,
    private errorHandler: GlobalErrorService,
    private fileDownloadService: FileDownloadService,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef,
    private openPositionsService: HiringManagerOpenpositionsService,
    private dialog: MatDialog,
  ) {
    effect(() => {
      const signalData = this.candidatesService.candidatesSignal();
      const payload = signalData?.data;
      if (payload?.data && Array.isArray(payload.data)) {
        this.candidates = payload.data;
        this.displayCandidatesData = payload.data;
        this.isDataLoaded = true;
        this.isDataChanging = true;
        this.paginationParams.total = payload.totalRecords;
        this.candidatesCount = payload.totalRecords;
        if (this.selectedTab) {
          this.selectedTab.badgeContent = payload.totalRecords.toString();
        }
        this.candidatesCountChange.emit(payload.totalRecords);
        this.candidatesDataChange.emit(payload.data);
        this.cdr.markForCheck();
        setTimeout(() => { this.isDataChanging = false; }, 100);
      }
    });

    // Setup debounced search
    this.searchSubject
      .pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged() // Only emit if value is different from previous
      )
      .subscribe((searchValue) => {
        this.performSearch(searchValue);
      });
  }

  ngOnInit() {
    this.selectedTab = this.tabsData[1];
    this.initLoadCount = 0;
    this.loadOpenPositions();
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['hiringRequestId'] && !changes['hiringRequestId'].firstChange) {
      this.loadCandidatesData();
    }
  }

  loadOpenPositions(): void {
    interval(500)
      .pipe(takeWhile(() => !this.isDataLoaded))
      .subscribe({
        next: () => this.fetchOpenPositions(),
        error: (error) => {
          this.isDataLoaded = true;
          this.errorHandler.logComponentError(error);
        },
      });
  }

  fetchOpenPositions(): void {
    const signalData = this.openPositionsService.openPositionsSignal();
    if (signalData?.data) {
      this.openPositions = signalData?.data?.map(mapHiringRequest) || [];
      this.isDataLoaded = true;
      this.hiringRequestId = this.openPositions?.map(pos => pos?.hiringRequestId);
      this.loadCandidatesData();
    }
  }

  loadCandidatesData(): void {
    this.initLoadCount = 0;
    this.currentServerPage = 1;
    this.candidatesService.getCandidates(this.hiringRequestId, 1, paginationParams.pageSize);
  }

  onPageChange(page: pageData): void {
    if (this.initLoadCount < this.INIT_FIRES) {
      this.initLoadCount++;
      return;
    }
    if (this.isDataChanging) return;
    this.candidatesService.getCandidates(
      this.hiringRequestId,
      page.currentPage,
      page.pageSize,
      this.currentSearchKey || undefined,
      this.currentSearchValue || undefined
    );
  }

  onSearchFieldChange(option: any): void {
    const incoming = typeof option === 'object' ? option?.value : option;
    if (incoming === this.selectedSearchField) {
      this.selectedSearchField = '';
      this.filterValue = '';
      this.currentSearchKey = '';
      this.currentSearchValue = '';
      this.searchFieldDropdownVisible = false;
      setTimeout(() => { this.searchFieldDropdownVisible = true; }, 0);
      this.currentServerPage = 1;
      this.candidatesService.getCandidates(this.hiringRequestId, 1, paginationParams.pageSize);
      return;
    }

    this.selectedSearchField = incoming;
    if (this.currentSearchValue) {
      this.currentSearchKey = this.selectedSearchField;
      this.currentServerPage = 1;
      this.candidatesService.getCandidates(
        this.hiringRequestId,
        1,
        paginationParams.pageSize,
        this.currentSearchKey,
        this.currentSearchValue
      );
    }
  }

  onSearchValueChange(value: string): void {
    if (this.isClearing) return;
    this.filterValue = value;
    const trimmed = value?.trim() ?? '';
    
    // Emit to search subject for debounced handling
    this.searchSubject.next(trimmed);
  }

  private performSearch(searchValue: string): void {
    if (searchValue.length > 2 || searchValue.length === 0) {
      // 'q' = server-side general search across all candidate columns
      // (handled by CandidateSpecification, same as the vendor flow).
      this.currentSearchKey = searchValue ? 'q' : '';
      this.currentSearchValue = searchValue;
      this.currentServerPage = 1;
      this.candidatesService.getCandidates(
        this.hiringRequestId,
        1,
        paginationParams.pageSize,
        this.currentSearchKey || undefined,
        this.currentSearchValue || undefined
      );
    }
  }

  clearInput(): void {
    this.isClearing = true;
    this.filterValue = '';
    this.filterText = '';
    this.currentSearchKey = '';
    this.currentSearchValue = '';
    this.isClearing = false;
    this.currentServerPage = 1;
    this.candidatesService.getCandidates(this.hiringRequestId, 1, paginationParams.pageSize);
  }

  getTotalCandidatesCount() {
    this.candidatesCount = this.searchService.getTotalCandidatesCount(
      this.displayCandidatesData,
      this.selectedTab,
      this.candidatesCountChange,
    );
  }

  handleDataFromChild(data: Candidates[]): void {
    this.displayCandidatesData = data;
    this.currentServerPage = 1;
    this.filterText = '';
    this.cdr.detectChanges();
  }

  // ---- op-table: sort ----
  sortBy(field: string): void {
    if (this.sortColumn === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = field;
      this.sortDir = 'asc';
    }
  }

  sortIndicator(field: string): string {
    return this.sortColumn === field ? this.sortDir : '';
  }

  get sortedRows(): any[] {
    const rows = [...(this.displayCandidatesData || [])] as any[];
    if (!this.sortColumn) return rows;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    const col = this.sortColumn;
    return rows.sort((a: any, b: any) => {
      const av = a?.[col];
      const bv = b?.[col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (col === 'createdDate' || col === 'earliestAvailability') {
        return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      }
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      if (
        (col === 'yearsOfExperience' || col === 'agingDays') &&
        !isNaN(an) &&
        !isNaN(bn)
      ) {
        return (an - bn) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }

  // ---- op-table: rows for the current server page (client-side sort within it) ----
  get pagedRows(): any[] {
    return this.sortedRows;
  }

  get totalRecords(): number {
    return this.paginationParams.total || 0;
  }

  get currentPageValue(): number {
    return this.currentServerPage;
  }

  get pageSizeValue(): number {
    return this.localPageSize;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.totalRecords || 0) / this.pageSizeValue));
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPageValue - 1) * this.pageSizeValue + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPageValue * this.pageSizeValue, this.totalRecords);
  }

  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.currentPageValue;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  // ---- op-table: server-driven pagination (fetches the requested page) ----
  private fetchPage(page: number): void {
    this.currentServerPage = page;
    this.candidatesService.getCandidates(
      this.hiringRequestId,
      page,
      this.localPageSize,
      this.currentSearchKey || undefined,
      this.currentSearchValue || undefined,
    );
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentServerPage) return;
    this.fetchPage(page);
  }

  prevPage(): void {
    this.goToPage(this.currentServerPage - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentServerPage + 1);
  }

  onPageSizeChange(event: Event): void {
    this.localPageSize = +(event.target as HTMLSelectElement).value;
    this.fetchPage(1);
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ---- op-table: aging pill tone ----
  agingClass(days?: number): string {
    const a = +(days || 0);
    return a > 90 ? 'stale' : a >= 30 ? 'mod' : 'fresh';
  }

  // ---- op-table: JS hover tooltip (only when a cell's text is truncated) ----
  showTip(event: MouseEvent, text?: string | null): void {
    if (!text) return;
    const cell = event.currentTarget as HTMLElement;
    const inner = (cell.querySelector('.op-tip-txt') as HTMLElement) || cell;
    if (inner.scrollWidth <= inner.clientWidth + 1) return;
    const rect = cell.getBoundingClientRect();
    this.hoverTip = { show: true, text, x: rect.left, y: rect.bottom + 4 };
  }

  hideTip(): void {
    this.hoverTip.show = false;
  }

  receiveData(data: string, candidateId: string) {
    this.displayCandidatesData?.forEach((item) => {
      if (item?.candidateId === candidateId) {
        item.status = data;
      }
    });
  }

  downloadFile(filePath: string, fileName: string, fileType: string) {
    const fullFileName = fileType + '_' + fileName + '.pdf';
    this.fileDownloadService.downloadFile(filePath, fullFileName);
  }

  viewHistory(eachRow: Candidates) {
    const mobile = (eachRow as any)?.contactNumber || '';
    if (!eachRow?.emailId) {
      this.errorMessage = 'Unable to fetch history: missing email.';
      return;
    }
    this.candidatesService.validateProfileHistory({
      email: eachRow.emailId,
      mobileNumber: mobile,
      labelName: 'history',
    }).subscribe({
      next: (res) => {
        if ((res as any)?.status === 'SUCCESS') {
          const data = (res as any)?.data;
          this.historyMessage = (res as any)?.message || data?.message || '';
          this.historySubmissions = Array.isArray(data?.submissions) ? data.submissions : [];
          this.historyDialogRef = this.dialog.open(this.historyDialogTpl, {
            width: '720px',
            maxWidth: '90vw',
            panelClass: 'center-dialog',
          });
        } else {
          this.errorMessage = (res as any)?.message || 'No history available';
        }
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Failed to load history';
      },
    });
  }

  closeHistoryDialog() {
    this.historyDialogRef?.close();
  }
}